import { memo } from 'react';
import clsx from 'clsx';
import { ComponentMemo } from '@headless-workspace/typings';
import { ComposableBannerCard, ComposableBannerCardProps, ComposableContainer } from '../../composables';

/**
 * GlowUnderBanner component renders a banner with various customizable elements.
 *
 * @param {string} className - Additional class names for styling the container.
 * @param {React.ReactNode} titleElement - The main title element of the banner.
 * @param {React.ReactNode} subTitleElement - The subtitle element of the banner.
 * @param {React.ReactNode} labelElement - The label element within the banner content.
 * @param {React.ReactNode} contentElement - The main content element of the banner.
 * @param {React.ReactNode} legalMentionElement - The legal mention element displayed at the bottom of the banner.
 * @param {React.ReactNode} button - The button element to be displayed in the banner.
 */
export const GlowUnderBanner: ComponentMemo<ComposableBannerCardProps> = memo(
    ({ className, title, subTitle, label, content, legalMention, button }: ComposableBannerCardProps) => {
        return (
            <ComposableBannerCard.Container className={clsx('flex-1', className)}>
                <ComposableBannerCard.MainBlock className={'p-1 pb-0.5 gap-0.5'}>
                    <ComposableContainer.Wrapper className={'flex flex-col gap-0.5'}>
                        <ComposableBannerCard.Title
                            content={title}
                            fontSize={'body'}
                            fontWeight={'bold'}
                            TagName={'h2'}
                            className={'line-clamp-2'}
                        />
                        <ComposableBannerCard.SubTitle content={subTitle} />
                    </ComposableContainer.Wrapper>
                    <ComposableBannerCard.Content
                        fontSize={'bodySmall'}
                        label={label}
                        content={content}
                        className={'line-clamp-2'}
                        text
                    />
                    <ComposableBannerCard.Button button={button} />
                    <ComposableBannerCard.MentionLabel>{legalMention}</ComposableBannerCard.MentionLabel>
                </ComposableBannerCard.MainBlock>
            </ComposableBannerCard.Container>
        );
    },
);

GlowUnderBanner.displayName = 'GlowUnderBanner';
